import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getInstituciones } from '../services/institucionesService';
import { getPublicacionesRecientes } from '../services/publicacionesService';
import { useInstitucion } from '../context/InstitucionContext';
import { PALETA_NEUTRA, derivarPaleta } from '../utils/paleta';
import MarcaPusah from '../components/MarcaPusah';
import PdfCoverThumbnail from '../components/PdfCoverThumbnail';
import { PORTAL } from '../config/portal';

// Portada del host de plataforma (sin institución en la ruta). Cumple dos papeles: presentar el
// servicio a quien llega de fuera y servir de directorio de las organizaciones que publican.
// Todos los textos comerciales salen de src/config/portal.js (pendientes de mercadeo).

const formatoFecha = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

// Sello de la organización dueña de una publicación.
const SelloInstitucion = ({ institucion }) => (
  <span className="flex items-center gap-1.5 min-w-0">
    {institucion.logoUrl ? (
      <img
        src={`${process.env.PUBLIC_URL}${institucion.logoUrl}`}
        alt=""
        className="h-4 w-auto max-w-[3.5rem] object-contain"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    ) : (
      <span
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: institucion.colorPrimario || PALETA_NEUTRA.primary }}
        aria-hidden="true"
      />
    )}
    <span className="truncate">{institucion.nombreCorto}</span>
  </span>
);

const TarjetaPublicacion = ({ publicacion }) => (
  <Link
    to={`/${publicacion.institucion.slug}/${publicacion.categoriaSlug}/${publicacion.slug}`}
    className="group flex flex-col bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100 hover:border-gray-300 hover:-translate-y-1"
  >
    <div
      className="relative w-full overflow-hidden flex-shrink-0"
      style={{
        aspectRatio: '3 / 4',
        background: `linear-gradient(135deg, ${publicacion.institucion.colorPrimario || PALETA_NEUTRA.primary}, ${publicacion.institucion.colorSecundario || PALETA_NEUTRA.headerGradientEnd})`,
      }}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <svg className="h-10 w-10 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      </div>
      <PdfCoverThumbnail pdfUrl={publicacion.rutaArchivo} />
    </div>
    <div className="p-3 flex flex-col gap-1 flex-1">
      <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
        {publicacion.descripcion || publicacion.titulo}
      </p>
      <p className="text-xs text-gray-400 line-clamp-1">{publicacion.titulo}</p>
      <div className="mt-auto pt-2 flex items-center justify-between gap-2 text-xs text-gray-500 border-t border-gray-100">
        <SelloInstitucion institucion={publicacion.institucion} />
        <span className="flex-shrink-0 text-gray-400">{formatoFecha(publicacion.fechaPublicacion)}</span>
      </div>
    </div>
  </Link>
);

const TarjetaInstitucion = ({ institucion }) => {
  const paleta = derivarPaleta({
    primario: institucion.colorPrimario,
    secundario: institucion.colorSecundario,
  });

  return (
    <Link
      to={`/${institucion.slug}`}
      className="group flex flex-col bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-200 hover:border-gray-300 overflow-hidden transition-all hover:-translate-y-1"
    >
      <div
        className="h-24 flex items-center justify-center px-6"
        style={{
          background: `linear-gradient(135deg, ${paleta.headerGradientStart}, ${paleta.headerGradientMid}, ${paleta.headerGradientEnd})`,
        }}
      >
        {institucion.logoUrl ? (
          <img
            src={`${process.env.PUBLIC_URL}${institucion.logoUrl}`}
            alt={`Logo ${institucion.nombreCorto}`}
            className="max-h-12 w-auto drop-shadow"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <span className="text-white font-bold text-xl drop-shadow">{institucion.nombreCorto}</span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-gray-900">{institucion.nombreCorto}</h3>
        <p className="text-sm text-gray-500 mt-0.5">{institucion.nombre}</p>
        {institucion.descripcion && (
          <p className="text-sm text-gray-400 mt-2 line-clamp-2">{institucion.descripcion}</p>
        )}
        <span
          className="mt-auto pt-4 font-semibold text-sm inline-flex items-center gap-1.5"
          style={{ color: paleta.ctaText }}
        >
          Ver publicaciones
          <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
};

const PortalPusah = () => {
  const { nombreMarca } = useInstitucion();
  const [instituciones, setInstituciones] = useState([]);
  const [recientes, setRecientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // La vitrina es accesoria: si falla, el directorio se muestra igual.
    Promise.all([
      getInstituciones(),
      getPublicacionesRecientes(PORTAL.vitrina.cantidad).catch(() => []),
    ])
      .then(([listaInstituciones, listaRecientes]) => {
        setInstituciones(listaInstituciones);
        setRecientes(listaRecientes);
      })
      .catch(() => setError('No se pudieron cargar las organizaciones'))
      .finally(() => setLoading(false));
  }, []);

  const correo = PORTAL.contacto.email;
  const enlaceCorreo = correo
    ? `mailto:${correo}?subject=${encodeURIComponent(PORTAL.contacto.asuntoCorreo || '')}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Presentación */}
      <header
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${PALETA_NEUTRA.headerGradientStart}, ${PALETA_NEUTRA.headerGradientEnd})` }}
      >
        <div className="absolute inset-0 bg-pattern opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <MarcaPusah variante="blanco" className="h-11 sm:h-12 drop-shadow-md" conEnlace={false} />
          <h1 className="mt-8 text-3xl sm:text-4xl font-extrabold text-white tracking-tight max-w-3xl drop-shadow-md">
            {PORTAL.hero.titular}
          </h1>
          <p className="mt-4 text-white/85 text-base sm:text-lg font-light max-w-2xl">
            {PORTAL.hero.subtitulo}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={PORTAL.hero.accionPrincipal.ancla}
              className="inline-flex items-center px-5 py-2.5 rounded-lg font-semibold text-sm bg-white text-gray-900 shadow-md hover:bg-gray-100 transition-colors"
            >
              {PORTAL.hero.accionPrincipal.texto}
            </a>
            {enlaceCorreo && (
              <a
                href={enlaceCorreo}
                className="inline-flex items-center px-5 py-2.5 rounded-lg font-semibold text-sm text-white bg-white/15 border border-white/25 hover:bg-white/25 transition-colors"
              >
                {PORTAL.hero.accionSecundaria.texto}
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="w-full">
        {/* Vitrina de publicaciones recientes */}
        {recientes.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{PORTAL.vitrina.titulo}</h2>
              <p className="text-gray-500">{PORTAL.vitrina.subtitulo}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {recientes.map((publicacion) => (
                <TarjetaPublicacion key={publicacion.id} publicacion={publicacion} />
              ))}
            </div>
          </section>
        )}

        {/* Directorio de organizaciones */}
        <section id="organizaciones" className="bg-white border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{PORTAL.directorio.titulo}</h2>
              <p className="text-gray-500">{PORTAL.directorio.subtitulo}</p>
            </div>

            {loading ? (
              <p className="text-center text-gray-400 py-8">Cargando...</p>
            ) : error ? (
              <p className="text-center text-red-500 py-8">{error}</p>
            ) : instituciones.length === 0 ? (
              <p className="text-center text-gray-400 py-8">{PORTAL.directorio.vacio}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {instituciones.map((institucion) => (
                  <TarjetaInstitucion key={institucion.id} institucion={institucion} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
            {PORTAL.comoFunciona.titulo}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PORTAL.comoFunciona.pasos.map((paso, indice) => (
              <div key={paso.titulo} className="bg-white rounded-2xl border border-gray-200 p-6">
                <span
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white font-bold text-sm mb-4"
                  style={{ backgroundColor: PALETA_NEUTRA.primary }}
                >
                  {indice + 1}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{paso.titulo}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{paso.descripcion}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contacto (los datos definitivos los define mercadeo en src/config/portal.js) */}
        {(PORTAL.contacto.descripcion || correo || PORTAL.contacto.telefono) && (
          <section className="bg-white border-t border-gray-200">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{PORTAL.contacto.titulo}</h2>
              {PORTAL.contacto.descripcion && (
                <p className="text-gray-500 mb-6">{PORTAL.contacto.descripcion}</p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-3">
                {enlaceCorreo && (
                  <a
                    href={enlaceCorreo}
                    className="inline-flex items-center px-5 py-2.5 rounded-lg font-semibold text-sm text-white shadow-md hover:opacity-90 transition-opacity"
                    style={{ background: `linear-gradient(135deg, ${PALETA_NEUTRA.accentFrom}, ${PALETA_NEUTRA.accentTo})` }}
                  >
                    {correo}
                  </a>
                )}
                {PORTAL.contacto.telefono && (
                  <a
                    href={`tel:${PORTAL.contacto.telefono.replace(/\s/g, '')}`}
                    className="inline-flex items-center px-5 py-2.5 rounded-lg font-semibold text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    {PORTAL.contacto.telefono}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="mt-auto bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-400">
          <p>© {new Date().getFullYear()} {nombreMarca}</p>
          <div className="flex items-center gap-4">
            {PORTAL.legal.aviso && <span>{PORTAL.legal.aviso}</span>}
            <Link to="/admin/login" className="hover:text-gray-600 transition-colors">
              Acceso para administradores
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PortalPusah;
